import { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { createContact, type Contact } from '@/api/people'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

/** "+ Add person" that turns into a name field, so a new person can be picked without leaving the form. */
export function AddPersonInline({ onAdded }: { onAdded: (contact: Contact) => void }) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: () => createContact({ name: name.trim() }),
    onSuccess: (contact) => {
      // Shown in the picker straight away, before the refetch lands.
      queryClient.setQueryData<Contact[]>(['contacts', { includeArchived: false }], (old) =>
        old && !old.some((c) => c.id === contact.id) ? [...old, contact] : old
      )
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
      setEditing(false)
      setName('')
      onAdded(contact)
    },
    onError: (err) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(detail ?? 'Could not add this person. Please try again.')
    },
  })

  function save() {
    if (!name.trim()) {
      setError('Enter a name')
      return
    }
    setError(null)
    mutation.mutate()
  }

  if (!editing) {
    return (
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-auto px-0 text-[var(--fl-primary-text)]"
        onClick={() => setEditing(true)}
      >
        <Plus size={14} /> Add person
      </Button>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-2">
        <Input
          aria-label="New person's name"
          placeholder="Name"
          maxLength={100}
          autoFocus
          value={name}
          onChange={(e) => {
            setError(null)
            setName(e.target.value)
          }}
          onKeyDown={(e) => {
            // Enter would otherwise submit the surrounding transaction form.
            if (e.key === 'Enter') {
              e.preventDefault()
              save()
            }
          }}
          className="flex-1"
        />
        <Button
          type="button"
          size="sm"
          aria-label="Save person"
          onClick={save}
          disabled={mutation.isPending}
        >
          {mutation.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => {
            setEditing(false)
            setName('')
            setError(null)
          }}
        >
          Cancel
        </Button>
      </div>
      {error && <p className="mt-1 text-xs text-[var(--fl-error)]">{error}</p>}
    </div>
  )
}
